import { useState, type ReactNode } from 'react'
import { Icon, type IconName } from './Icon'
import { Sidebar, type RowActions } from './Sidebar'
import { Switcher } from './Switcher'
import type { ProjectSummary, State, Workspace } from '../api'
import type { DropTarget, NavPath } from '../nav-tree'
import type { Place } from '../router'
import type { Theme } from '../theme'
import { useDialog } from '../useDialog'
import { useEscape } from '../useEscape'

/**
 * The one sidebar, on every screen.
 *
 * The shell used to be an app rail of twelve places beside a second column
 * that only Pages had — so the page tree, which is the product's actual map,
 * was a room inside a room. This column is the GitBook anatomy instead: the
 * switcher, a search row, the three places a writer checks (Home, Inbox,
 * Pulse), and then the tree itself filling the rest, behind a Pages | Library
 * pair of tabs. Library, People and Settings keep compact rows at the foot.
 *
 * Everything that left the rail is still reachable: Import, Variables,
 * Redirects, Words & style, Search and Analytics are rows at the top of
 * Settings, and Analytics also stays a door on Home's Readers card. CLI & MCP
 * returned to the rail as Developers once it became a daily integration
 * surface rather than an administrative setting. `SETTINGS_PLACES` in
 * `tools/lib/shell.mjs` is the list the gates walk.
 */

/**
 * What the whole-site menu offers: three of the site's files, and the trash.
 *
 * Three of six on purpose, and named `MENU` rather than `SITE_FILES` so that
 * is legible. Variables, Search and Redirects each have a SCREEN of their own
 * at the top of Settings, which is a better door than the raw YAML; the
 * navigation has no screen, and the glossary and the style guide keep a
 * shortcut here as well as one on the Words screen. The full set is
 * `SITE_FILES` in `@staleless/schema`, and this is deliberately not it — the
 * previous name invited exactly the reading that a list of three was the list
 * of all of them.
 */
const SITE_FILE_MENU: { path: string; icon: IconName; label: string; hint: string }[] = [
  {
    path: 'nav.yaml',
    icon: 'nav',
    label: 'Edit navigation file',
    hint: 'Ordering for the whole site',
  },
  {
    path: 'glossary.yaml',
    icon: 'quote',
    label: 'Words',
    hint: 'The words this documentation uses',
  },
  { path: 'style.yaml', icon: 'text', label: 'Style', hint: 'How this documentation is written' },
]

export function SideNav({
  place,
  onPlace,
  folded,
  onFold,
  mobileOpen,
  onMobileClose,
  inboxCount,
  onInbox,
  onSearch,
  onPeople,
  bell,
  state,
  selected,
  onSelectFile,
  onNewPage,
  onReorder,
  onTrash,
  readOnly,
  rowActions,
  workspaceName,
  siteName,
  workspaces,
  currentWorkspace,
  onSwitchWorkspace,
  projects,
  project,
  onSwitchProject,
  mayManageSites,
  mayAddSite,
  onNewSite,
  onUpgrade,
  onInvite,
  theme,
  onTheme,
  onSignOut,
}: {
  place: Place
  onPlace: (next: Place) => void
  folded: boolean
  onFold: (folded: boolean) => void
  /** ≤720px: the column is a drawer behind the top bar's menu button. */
  mobileOpen: boolean
  onMobileClose: () => void
  inboxCount: number
  onInbox: () => void
  onSearch: () => void
  onPeople: () => void
  /** The notifications bell, handed in whole — trigger and drawer together. */
  bell: ReactNode
  state: State
  selected: string | null
  onSelectFile: (path: string) => void
  onNewPage: () => void
  onReorder: (from: NavPath, to: DropTarget) => void
  onTrash: () => void
  readOnly: boolean
  /** What a row can do to itself; absent for anybody who may not write. */
  rowActions?: RowActions
  workspaceName: string
  siteName: string | null
  workspaces: Workspace[]
  currentWorkspace: string | null
  onSwitchWorkspace: (slug: string) => void
  projects: ProjectSummary[]
  project: string | null
  onSwitchProject: (slug: string) => void
  /** Role gate: unlike a plan gate, this may keep the creation control absent. */
  mayManageSites: boolean
  mayAddSite: boolean
  onNewSite: () => void
  onUpgrade: () => void
  onInvite: () => void
  theme: Theme
  onTheme: (next: Theme) => void
  onSignOut: () => void
}) {
  /**
   * Which tab holds the lower half. Derived, not held: the Library tab IS the
   * Library place, so a click anywhere that lands somewhere else brings the
   * tree back without this component keeping a second copy of where we are.
   */
  const tab: 'pages' | 'library' = place === 'library' ? 'library' : 'pages'
  const [filesOpen, setFilesOpen] = useState(false)
  const filesPanel = useDialog<HTMLDivElement>(filesOpen, () => setFilesOpen(false))
  // The drawer at phone width closes on Escape like every other overlay.
  useEscape(mobileOpen, onMobileClose)

  /** Navigation from inside the drawer also closes the drawer. */
  const go = (next: Place) => {
    onPlace(next)
    onMobileClose()
  }

  const badge = (count: number) =>
    count > 0 ? <span className="sidenav__badge">{count}</span> : null

  const placeRow = (id: Place, icon: IconName, label: string, count = 0) => (
    <button
      className={`sidenav__row${place === id ? ' is-on' : ''}`}
      data-nav={id}
      aria-current={place === id ? 'page' : undefined}
      onClick={() => go(id)}
    >
      <Icon name={icon} size={14} />
      <span className="sidenav__rowlabel truncate">{label}</span>
      {badge(count)}
    </button>
  )

  if (folded && !mobileOpen) {
    /**
     * The 48px strip: the same doors, as icons. The tree waits behind the
     * Pages icon — a tree squeezed to icon width would be rows of dots.
     * Never at phone width: there the drawer is the whole navigation, so an
     * open drawer always shows the full column, folded or not.
     */
    return (
      <nav className="sidenav sidenav--folded" aria-label="Application">
        <button
          className="sidenav__stripbtn sidenav__striplogo"
          data-tip={`Expand the sidebar — ${workspaceName}  ⌘\\`}
          onClick={() => onFold(false)}
        >
          <span className="brand__mark">
            <Icon name="logo" size={13} label="Expand the sidebar" />
          </span>
        </button>
        <button
          className={`sidenav__stripbtn${place === 'home' ? ' is-on' : ''}`}
          data-nav="home"
          data-tip="Home"
          onClick={() => go('home')}
        >
          <Icon name="monitor" size={14} label="Home" />
        </button>
        <button
          className="sidenav__stripbtn"
          data-nav="inbox"
          data-tip="What Pulse thinks needs a look"
          onClick={onInbox}
        >
          <Icon name="alert" size={14} label="Inbox" />
          {badge(inboxCount)}
        </button>
        <button
          className={`sidenav__stripbtn${place === 'pulse' ? ' is-on' : ''}`}
          data-nav="pulse"
          data-tip="Pulse"
          onClick={() => go('pulse')}
        >
          <Icon name="pulse" size={14} label="Pulse" />
        </button>
        <button
          className={`sidenav__stripbtn${place === 'pages' ? ' is-on' : ''}`}
          data-nav="pages"
          data-tip="Pages"
          onClick={() => go('pages')}
        >
          <Icon name="pages" size={14} label="Pages" />
        </button>
        <span className="sidenav__stripspacer" />
        <button
          className="sidenav__stripbtn"
          data-tip="Expand the sidebar  ⌘\"
          onClick={() => onFold(false)}
        >
          <Icon name="chevron-right" size={14} label="Expand the sidebar" />
        </button>
      </nav>
    )
  }

  return (
    <nav className={`sidenav${mobileOpen ? ' sidenav--open' : ''}`} aria-label="Application">
      <div className="sidenav__head">
        <Switcher
          workspaceName={workspaceName}
          siteName={siteName}
          workspaces={workspaces}
          currentWorkspace={currentWorkspace}
          onSwitchWorkspace={onSwitchWorkspace}
          projects={projects}
          project={project}
          onSwitchProject={onSwitchProject}
          mayManageSites={mayManageSites}
          mayAddSite={mayAddSite}
          onNewSite={onNewSite}
          onUpgrade={onUpgrade}
          onSettings={() => go('settings')}
          onInvite={onInvite}
          theme={theme}
          onTheme={onTheme}
          onSignOut={onSignOut}
        />
        {bell}
        <button
          className="btn btn--ghost btn--icon btn--sm sidenav__fold"
          data-tip="Collapse the sidebar  ⌘\"
          onClick={() => onFold(true)}
        >
          <Icon name="chevron-left" size={13} label="Collapse the sidebar" />
        </button>
      </div>

      <button className="sidenav__search" onClick={onSearch}>
        <Icon name="search" size={13} />
        <span className="sidenav__searchwords truncate">Search or jump to…</span>
        <kbd>⌘K</kbd>
      </button>

      <div className="sidenav__places">
        {placeRow('home', 'monitor', 'Home')}
        <button
          className="sidenav__row"
          data-nav="inbox"
          data-tip="What Pulse thinks needs a look"
          onClick={() => {
            onMobileClose()
            onInbox()
          }}
        >
          <Icon name="alert" size={14} />
          <span className="sidenav__rowlabel truncate">Inbox</span>
          {badge(inboxCount)}
        </button>
        {placeRow('pulse', 'pulse', 'Pulse', inboxCount)}
      </div>

      <div className="sidenav__tabs">
        {/* The tablist holds tabs and nothing else. The spacer and the
            whole-site menu beside it are not tabs, and a tablist that
            contains them is a promise to a screen reader that the product
            does not keep — axe calls it `aria-required-children`. */}
        <div className="sidenav__tablist" role="tablist" aria-label="Sidebar sections">
          <button
            role="tab"
            aria-selected={tab === 'pages'}
            data-nav="pages"
            className={`sidenav__tab${tab === 'pages' ? ' is-active' : ''}`}
            onClick={() => go('pages')}
          >
            Pages
          </button>
          <button
            role="tab"
            aria-selected={tab === 'library'}
            className={`sidenav__tab${tab === 'library' ? ' is-active' : ''}`}
            onClick={() => go('library')}
          >
            Library
          </button>
        </div>
        <span className="sidenav__tabspacer" />
        {tab === 'pages' && (
          <div className="sidenav__files">
            <button
              className="btn btn--ghost btn--icon btn--sm"
              aria-haspopup="dialog"
              aria-expanded={filesOpen}
              data-tip="The whole site — navigation, words, style, trash"
              onClick={() => setFilesOpen((v) => !v)}
            >
              <span className="sidenav__dots" aria-hidden>
                ⋯
              </span>
              <span className="sr-only">The whole site</span>
            </button>
            {filesOpen && (
              <>
                <div className="popover-scrim" onMouseDown={() => setFilesOpen(false)} />
                <div
                  className="switcher__menu sidenav__filesmenu"
                  role="dialog"
                  aria-label="The whole site"
                  ref={filesPanel}
                >
                  {SITE_FILE_MENU.map((f) => (
                    <button
                      key={f.path}
                      className={`switcher__item${selected === f.path ? ' is-on' : ''}`}
                      data-tip={f.hint}
                      onClick={() => {
                        setFilesOpen(false)
                        onSelectFile(f.path)
                        onMobileClose()
                      }}
                    >
                      <Icon name={f.icon} size={13} />
                      {f.label}
                    </button>
                  ))}
                  {rowActions && (
                    <button
                      className="switcher__item"
                      data-tip="A heading in the sidebar, for pages to go under"
                      onClick={() => {
                        setFilesOpen(false)
                        rowActions.newSection()
                      }}
                    >
                      <Icon name="plus" size={13} />
                      New section
                    </button>
                  )}
                  <button
                    className="switcher__item"
                    data-tip="Deleted pages, and the way back"
                    onClick={() => {
                      setFilesOpen(false)
                      onTrash()
                    }}
                  >
                    <Icon name="trash" size={13} />
                    Trash
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {tab === 'pages' ? (
        <Sidebar
          state={state}
          selected={selected}
          onSelect={(p) => {
            onSelectFile(p)
            onMobileClose()
          }}
          onNewPage={onNewPage}
          onReorder={onReorder}
          readOnly={readOnly}
          {...(rowActions ? { actions: rowActions } : {})}
        />
      ) : (
        <p className="sidenav__note">
          The library is open: every image pages share, and where each one is used. Pages brings the
          tree back.
        </p>
      )}

      <div className="sidenav__foot">
        {placeRow('library', 'image', 'Library')}
        {placeRow('developers', 'code', 'Developers')}
        {placeRow('help', 'callout', 'Help & docs')}
        <button
          className="sidenav__row"
          data-nav="people"
          data-tip="Who can reach this workspace"
          onClick={() => {
            onMobileClose()
            onPeople()
          }}
        >
          <Icon name="people" size={14} />
          <span className="sidenav__rowlabel truncate">People</span>
        </button>
        {placeRow('settings', 'nav', 'Settings')}
      </div>
    </nav>
  )
}
